import type { Insurance } from "@/types/insurance/insurance";
import { toFixedNumber } from "../utils";

const MIN_INVALIDITY = 0.01;

const parseAgeBounds = (insurance: Insurance | undefined) => {
	const minAge = parseFloat(
		insurance?.params?.find((p) => p.type === "min-age")?.value as string,
	);
	const maxAge = parseFloat(
		insurance?.params?.find((p) => p.type === "max-age")?.value as string,
	);
	return {
		minAge: Number.isFinite(minAge) ? minAge : NaN,
		maxAge: Number.isFinite(maxAge) ? maxAge : NaN,
	};
};

const parseBaseCompensationG = (insurance: Insurance | undefined) => {
	const raw = insurance?.params?.find((p) => p.type === "base-compensation")
		?.value as string | undefined;
	const v = parseFloat(raw ?? "");
	return Number.isFinite(v) && v >= 0 ? v : NaN;
};

const ageInInclusiveRange = (
	age: number,
	minAge: number,
	maxAge: number,
): boolean => {
	const a = Math.floor(age);
	return a >= minAge && a <= maxAge;
};

/** Proportional from 1% invalidity; no age-based adjustment; G scale. */
export const collectiveAccidentInvalidityCompensation = (
	insurance: Insurance | undefined,
	age: number,
	invalidity: number,
): number => {
	if (!insurance || insurance.type !== "collective-accident-invalidity") {
		return 0;
	}

	const baseG = parseBaseCompensationG(insurance);
	const { minAge, maxAge } = parseAgeBounds(insurance);

	if (
		Number.isNaN(baseG) ||
		Number.isNaN(minAge) ||
		Number.isNaN(maxAge) ||
		!ageInInclusiveRange(age, minAge, maxAge)
	) {
		return 0;
	}

	if (typeof invalidity !== "number" || Number.isNaN(invalidity)) {
		return 0;
	}

	if (invalidity < MIN_INVALIDITY || invalidity > 1) {
		return 0;
	}

	return toFixedNumber(baseG * invalidity);
};

/** Flat G when age in range; not coordinated with group life. */
export const collectiveAccidentDeathCompensation = (
	insurance: Insurance | undefined,
	age: number,
): number => {
	if (!insurance || insurance.type !== "collective-accident-death") {
		return 0;
	}

	const baseG = parseBaseCompensationG(insurance);
	const { minAge, maxAge } = parseAgeBounds(insurance);

	if (
		Number.isNaN(baseG) ||
		Number.isNaN(minAge) ||
		Number.isNaN(maxAge) ||
		!ageInInclusiveRange(age, minAge, maxAge)
	) {
		return 0;
	}

	return toFixedNumber(baseG);
};
